import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderMinValidator,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderSelectComponent,
  ElderShellService,
  ElderSinglePaneWrapperComponent,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { EMPTY } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { FacilitySelectDirective } from '../../facilities/facility-select.directive'
import { costToSeverity, CostType, Severity } from '../CostSeverity'
import { emptySettings, JsspSettings } from '../jssp-settings'
import { JsspSettingsService } from '../jssp-settings.service'

import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatToolbar } from '@angular/material/toolbar'

@Component({
  selector: 'app-jssp-settings-side-detail',
  templateUrl: './jssp-settings-side-detail.component.html',
  styleUrls: ['./jssp-settings-side-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    ElderHeaderComponent,
    FormsModule,
    MatFormField,
    ElderSelectComponent,
    FacilitySelectDirective,
    MatLabel,
    MatError,
    ElderValidationErrorDirective,
    MatInput,
    ElderMinValidator,
    MatSuffix,
    MatTooltip,
    MatHint,
    ElderTranslatePipe,
  ],
})
export class JsspSettingsSideDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/
  private jsspSettingsService = inject(JsspSettingsService)
  private toastService = inject(ElderToastService)
  private shellService = inject(ElderShellService)
  public settings: JsspSettings
  public CostType = CostType

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.settings = route.snapshot.data['jsspSettings'] ?? emptySettings()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  onSave() {
    if (this.settings.id) {
      this.jsspSettingsService
        .update(this.settings)
        .pipe(this.handlePersist())
        .pipe(this.handleError('messages.updating_failed'))
        .subscribe()
    } else {
      this.jsspSettingsService
        .create(this.settings)
        .pipe(this.handlePersist())
        .pipe(this.handleError('messages.persisting_failed'))
        .subscribe()
    }
  }

  onClose() {
    this.shellService.closeSideContent()
  }

  public showWarning(involvedWcCost: number, costType: CostType): boolean {
    const severity = costToSeverity(involvedWcCost, costType)
    return severity === Severity.HIGH || severity === Severity.LOW
  }

  public getToolTip(involvedWcCost: number, costType: CostType): string {
    const severity = costToSeverity(involvedWcCost, costType)
    if (severity === Severity.HIGH) {
      return 'jssp.highCost'
    } else if (severity === Severity.LOW) {
      return 'jssp.lowCost'
    } else {
      throw new Error('Assertion error')
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private handlePersist() {
    return tap((updated: JsspSettings) => {
      this.settings = updated
      this.toastService.info('messages.persisting_successful')
    })
  }

  private handleError(message: string) {
    return catchError((err) => {
      this.toastService.error(message, err)
      return EMPTY
    })
  }
}
